import { Injectable, InjectionToken, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import * as SocketIo from 'socket.io-client';

import { environment } from '@gfe/../environments/environment';
import { GbeTokensService } from '@gfe/core/services/gbe-tokens/gbe-tokens.service';

import { CODED_MESSAGE_VERBATIM_KEYS } from '@core/models/coded-status-message.model';
import { RawKpi } from '@core/models/kpi.model';
import { PlanEditIntent } from '@core/models/plan-edit-gesture.model';
import {
  PlanWatchHolder,
  RENDERED_FLAVOURS,
  WatchPatch,
  WatchPatchEvent,
  WatchPatchUpsert
} from '@core/models/plan-edit.model';
import {
  PlanFinding,
  PlanFindingsWire,
  findingsFromWire
} from '@core/models/plan-finding.model';
import { PlanEditSessionActions } from '@core/store/plan-edit/plan-edit-session.actions';
import { PlanWatchActions } from '@core/store/plan-edit/plan-edit.actions';
import { convertKeysToCamelCase } from '@core/utils/case-conversions';

export interface PlanEditSocket {
  readonly connected: boolean;
  /** False once the server refused the connection: socket.io then stops reconnecting. */
  readonly active: boolean;
  on(event: string, handler: (payload?: unknown) => void): unknown;
  emit(event: string, payload?: unknown): unknown;
  connect(): unknown;
  disconnect(): unknown;
}

export type PlanEditSocketFactory = (
  url: string,
  options: { auth: (cb: (data: object) => void) => void; transports: string[] }
) => PlanEditSocket;

export const PLAN_EDIT_SOCKET_FACTORY = new InjectionToken<PlanEditSocketFactory>(
  'PLAN_EDIT_SOCKET_FACTORY',
  {
    providedIn: 'root',
    factory: () => (url, options) => SocketIo.io(url, options) as unknown as PlanEditSocket
  }
);

const TOKEN_MIN_TTL_MS = 30_000;
const RETRY_FIRST_DELAY_MS = 2_000;
const RETRY_MAX_DELAY_MS = 30_000;

interface UpsertWire {
  entityType: string;
  userEntityType: string;
  uid: string;
  jsonData: string;
}

interface WatchWire {
  planId: string;
  holder?: PlanWatchHolder | null;
  projections?: string[];
  live?: boolean;
  revision?: number;
  planDataRevision?: string;
  patch?: { upserted?: UpsertWire[]; removed?: { entityType: string; uid: string }[] };
  findings?: PlanFindingsWire | null;
  recomputePending?: boolean;
  planKpis?: RawKpi[];
  reason?: string;
  errorCode?: string;
  errorMessage?: string;
  sessionId?: string;
  resumable?: boolean;
  appliedClientSeq?: number;
  mintedRouteUids?: Record<string, string>;
  clientSeq?: number;
  intentIndex?: number;
  details?: Record<string, unknown>;
  runId?: string;
}

/**
 * The `/plan-edit` namespace, watch side. Opened on the same URL as `WebsocketService` and without
 * `forceNew`, so socket.io multiplexes it over that connection.
 */
@Injectable({ providedIn: 'root' })
export class PlanEditSocketService {
  private readonly store = inject(Store);
  private readonly tokens = inject(GbeTokensService);
  private readonly createSocket = inject(PLAN_EDIT_SOCKET_FACTORY);
  private socket: PlanEditSocket | null = null;
  private refusals = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  /** Sent only on a live connection: every connection is announced, and the effects watch then. */
  watch(planId: string): void {
    this.sendOnConnection('plan-edit:watch', { plan_id: planId });
  }

  unwatch(): void {
    if (this.socket?.connected) this.socket.emit('plan-edit:unwatch');
  }

  /** Like `watch`, opens the socket when this tab has none; the effects open again on connection. */
  openSession(planId: string, paramId: number): void {
    this.sendOnConnection('plan-edit:open', {
      plan_id: planId,
      param_id: paramId,
      projections: RENDERED_FLAVOURS
    });
  }

  resumeSession(sessionId: string): boolean {
    return this.send('plan-edit:resume', { session_id: sessionId });
  }

  /** One gesture is one call: DL applies the intents whole or not at all. */
  mutate(clientSeq: number, intents: PlanEditIntent[]): boolean {
    return this.send('plan-edit:mutate', {
      client_seq: clientSeq,
      intents: intents.map((i) => ({
        intent_type: i.intentType,
        json_data: JSON.stringify(i.payload)
      }))
    });
  }

  undo(clientSeq: number): boolean {
    return this.send('plan-edit:undo', { client_seq: clientSeq });
  }

  redo(clientSeq: number): boolean {
    return this.send('plan-edit:redo', { client_seq: clientSeq });
  }

  save(clientSeq: number): boolean {
    return this.send('plan-edit:save', { client_seq: clientSeq });
  }

  heartbeat(): boolean {
    return this.send('plan-edit:heartbeat');
  }

  /** Ends the edit session; `close()` is the one that drops the socket. */
  closeSession(): boolean {
    return this.send('plan-edit:close');
  }

  /** False when the socket is not connected: the command is dropped, not queued. */
  private send(event: string, payload?: unknown): boolean {
    if (!this.socket?.connected) return false;
    this.socket.emit(event, payload);
    return true;
  }

  private sendOnConnection(event: string, payload: unknown): void {
    const socket = this.socket ?? this.open();
    if (socket.connected) socket.emit(event, payload);
    else if (!socket.active) socket.connect();
  }

  close(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.cancelRetry();
  }

  /**
   * socket.io gives up on a connection the server closed, so it is reopened here. The backoff is
   * reset by a watch answer rather than by `connect`: GBE announces a connection before it checks
   * the token, so a refused client sees `connect` before every refusal.
   */
  private retry(socket: PlanEditSocket): void {
    if (socket.active || this.retryTimer !== null) return;
    const delay = Math.min(RETRY_FIRST_DELAY_MS * 2 ** this.refusals, RETRY_MAX_DELAY_MS);
    this.refusals++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.socket === socket && !socket.active) socket.connect();
    }, delay);
  }

  private cancelRetry(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.refusals = 0;
  }

  private open(): PlanEditSocket {
    const socket = this.createSocket(environment.gbeWebSocketUrl + '/plan-edit', {
      // Called on every (re)connection: GBE authenticates once, at connection.
      auth: (cb) =>
        this.tokens
          .getFreshAccessToken(TOKEN_MIN_TTL_MS)
          .subscribe((token) => cb({ token })),
      transports: ['websocket']
    });
    this.socket = socket;

    socket.on('connect', () => this.store.dispatch(PlanWatchActions.socketConnected()));
    socket.on('connect_error', () => this.retry(socket));
    socket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect') this.retry(socket);
      if (reason !== 'io client disconnect') {
        this.store.dispatch(PlanEditSessionActions.connectionLost());
      }
    });
    socket.on('plan-edit:watching', (raw) => {
      this.refusals = 0;
      const w = envelope(raw);
      this.store.dispatch(
        PlanWatchActions.watching({
          planId: w.planId,
          holder: w.holder ?? null,
          projections: w.holder && Array.isArray(w.projections) ? w.projections : [],
          live: w.live === true,
          snapshot:
            w.live === true
              ? {
                  revision: w.revision ?? 0,
                  recomputePending: w.recomputePending === true,
                  patch: patchFrom(w.patch),
                  findings: findingsFrom(w.findings),
                  ...kpisFrom(w)
                }
              : null
        })
      );
    });
    socket.on('plan-edit:watch-started', (raw) => {
      const w = envelope(raw);
      if (!w.holder) return;
      this.store.dispatch(
        PlanWatchActions.watchStarted({ planId: w.planId, holder: w.holder })
      );
    });
    socket.on('plan-edit:watch-patch', (raw) => {
      const w = envelope(raw);
      this.store.dispatch(
        PlanWatchActions.watchPatch({
          planId: w.planId,
          revision: w.revision ?? 0,
          recomputePending: w.recomputePending === true,
          patch: patchFrom(w.patch),
          findings: findingsFrom(w.findings),
          ...kpisFrom(w)
        })
      );
    });
    socket.on('plan-edit:watch-saved', (raw) => {
      const w = envelope(raw);
      this.store.dispatch(
        PlanWatchActions.watchSaved({
          planId: w.planId,
          revision: w.revision ?? 0,
          planDataRevision: w.planDataRevision ?? ''
        })
      );
    });
    socket.on('plan-edit:watch-ended', (raw) => {
      const w = envelope(raw);
      this.store.dispatch(
        PlanWatchActions.watchEnded({
          planId: w.planId,
          reason: w.reason === 'lost' ? 'lost' : 'closed'
        })
      );
    });
    socket.on('plan-edit:watch-recompute-failed', (raw) => {
      const w = envelope(raw);
      this.store.dispatch(PlanWatchActions.watchRecomputeFailed({ planId: w.planId }));
    });
    socket.on('plan-edit:watch-rejected', (raw) => {
      const w = envelope(raw);
      console.warn('[plan watch] rejected', w.errorCode);
      this.store.dispatch(PlanWatchActions.watchRejected({ errorCode: w.errorCode ?? '' }));
    });
    socket.on('plan-edit:opened', (raw) => {
      const w = envelope(raw);
      this.store.dispatch(
        PlanEditSessionActions.opened({
          sessionId: w.sessionId ?? '',
          revision: w.revision ?? 0,
          resumable: w.resumable === true
        })
      );
    });
    socket.on('plan-edit:resumed', (raw) => {
      const w = envelope(raw);
      this.store.dispatch(
        PlanEditSessionActions.resumed({ sessionId: w.sessionId ?? '', ...eventFrom(w) })
      );
    });
    socket.on('plan-edit:applied', (raw) => {
      const w = envelope(raw);
      this.store.dispatch(
        PlanEditSessionActions.applied({
          appliedClientSeq: w.appliedClientSeq ?? 0,
          ...eventFrom(w),
          mintedRouteUids: w.mintedRouteUids ?? {}
        })
      );
    });
    socket.on('plan-edit:recomputed', (raw) => {
      const { recomputePending: _pending, ...event } = eventFrom(envelope(raw));
      this.store.dispatch(PlanEditSessionActions.recomputed(event));
    });
    socket.on('plan-edit:rejected', (raw) => {
      const w = envelope(raw);
      this.store.dispatch(
        PlanEditSessionActions.rejected({
          clientSeq: w.clientSeq ?? null,
          intentIndex: w.intentIndex ?? null,
          errorCode: w.errorCode ?? '',
          errorMessage: w.errorMessage ?? '',
          details: w.details ?? {}
        })
      );
    });
    socket.on('plan-edit:saved', (raw) => {
      const w = envelope(raw);
      this.store.dispatch(
        PlanEditSessionActions.saved({ planDataRevision: w.planDataRevision ?? '' })
      );
    });
    socket.on('plan-edit:save-rejected', (raw) => {
      const w = envelope(raw);
      this.store.dispatch(
        PlanEditSessionActions.saveRejected({
          clientSeq: w.clientSeq ?? 0,
          errorCode: w.errorCode ?? '',
          errorMessage: w.errorMessage ?? '',
          details: w.details ?? {}
        })
      );
    });
    socket.on('plan-edit:closed', (raw) => {
      const w = envelope(raw);
      this.store.dispatch(PlanEditSessionActions.closed({ reason: w.reason ?? '' }));
    });
    return socket;
  }
}

function eventFrom(w: WatchWire): WatchPatchEvent {
  return {
    revision: w.revision ?? 0,
    recomputePending: w.recomputePending === true,
    patch: patchFrom(w.patch),
    findings: findingsFrom(w.findings),
    ...kpisFrom(w)
  };
}

/** The minted uids are keyed by the client's own temporary route keys, which DL holds opaque. */
const ENVELOPE_VERBATIM_KEYS = [...CODED_MESSAGE_VERBATIM_KEYS, 'minted_route_uids'];

function envelope(raw: unknown): WatchWire {
  return convertKeysToCamelCase(raw, ENVELOPE_VERBATIM_KEYS) as WatchWire;
}

function findingsFrom(wire: PlanFindingsWire | null | undefined): PlanFinding[] | null {
  return wire ? findingsFromWire(wire).findings : null;
}

/** No key at all when GBE sent none: an absent field keeps the KPIs the watch already has. */
function kpisFrom(w: WatchWire): { planKpis?: RawKpi[] } {
  return Array.isArray(w.planKpis) ? { planKpis: w.planKpis } : {};
}

/** `json_data` is parsed, not camel-cased: entity models read DL's snake_case keys. */
function patchFrom(wire: WatchWire['patch']): WatchPatch {
  const upserted: WatchPatchUpsert[] = [];
  for (const u of wire?.upserted ?? []) {
    try {
      upserted.push({
        entityType: u.entityType,
        userEntityType: u.userEntityType,
        uid: u.uid,
        data: JSON.parse(u.jsonData) as Record<string, unknown>
      });
    } catch {
      console.warn(`[plan watch] unreadable ${u.userEntityType} ${u.uid}`);
    }
  }
  return { upserted, removed: wire?.removed ?? [] };
}
